/**
 * daily-final-state — единый источник «финальное состояние доски дня» для
 * done-state (core-daily-board.md §2.3-DONE). Здесь живёт ВСЯ логика получения
 * финальной доски + показателей, экран её только монтирует (ствол→ветки,
 * owner-directive 2026-05-30).
 *
 * Финальная доска детерминированно выводится из `seed + moves[]` каноном-движком
 * (тот же прогон, что в реплее — клиент=сервер). Два пути:
 *   1. **Снимок при финише** (`snapshotFinalBoard`) — кладётся в `PlayedRecord`
 *      на своём устройстве → done-state рисуется мгновенно, без сети, переживает F5.
 *   2. **Пересимуляция при промахе кэша** (`resimulateFinalBoard` /
 *      `fetchFinalBoardByShortId`) — на чужом устройстве: тянем свой реплей по
 *      `short_id` и прогоняем движком. В БД ничего нового не пишется — `replays`
 *      уже хранит seed+moves (owner 2026-06-09).
 *
 * Показатели done-state — `fetchOwnDailyStats`: Ранг + Лучший счёт + свой
 * `short_id` (для «Пересмотреть») из ОДНОГО вызова `GET /daily/leaderboard`
 * (серверный блок `me`, §6.7 — своя строка вне пагинации).
 *
 * Cross-references:
 *   - docs/specs/core-daily-board.md §2.3-DONE / §6.7.
 *   - services/daily-cache.ts (FinalBoardSnapshot / PlayedRecord).
 *   - services/leaderboard-api.ts (getDailyLeaderboard → me + rows[0]).
 *   - services/replay-api.ts (getReplay → playable seed+moves).
 *   - @lines98/shared engine (startState + applyMove — канон).
 */

import { applyMove, startState } from '@lines98/shared';
import type { CellValue, FinishReason, Move, NextBall } from '@lines98/shared/types/game';

import type { InProgressBoardView } from './daily-played-snapshot.js';
import type { FinalBoardSnapshot } from '../services/daily-cache.js';
import { getDailyLeaderboard } from '../services/leaderboard-api.js';
import { getReplay } from '../services/replay-api.js';

/** Восстановленное финальное состояние партии (доска + итоговые числа). */
export interface ResimulatedFinalBoard {
  readonly board: FinalBoardSnapshot;
  /** Итоговый счёт (= score_verified по детерминизму — anti-cheat invariant). */
  readonly score: number;
  /** Собрано линий. */
  readonly lines: number;
}

/** Показатели done-state из дневного лидерборда (своя строка `me` + топ). */
export interface OwnDailyStats {
  /** Ранг игрока в дне (1-based). */
  readonly rank: number;
  /** Лучший счёт дня (верхняя строка лидерборда). */
  readonly bestScore: number;
  /** Свой score_verified (server-canonical). */
  readonly scoreVerified: number;
  /** Свои собранные линии. */
  readonly linesVerified: number;
  /** Ключ своего реплея — для «Пересмотреть» / пересимуляции на чужом устройстве. */
  readonly shortId: string;
}

/**
 * Снять снимок финальной доски из текущего состояния движка (вызывается в момент
 * финиша, пока стор держит финал). Глубоко копирует grid + next, чтобы снимок не
 * мутировал вслед за стором. `next` служит И нишей, И predict-призраками — ровно
 * как в игре (owner 2026-06-09: «оставить как снимок»).
 */
export function snapshotFinalBoard(
  grid: readonly CellValue[][],
  next: readonly NextBall[],
  movesCount: number,
  finishReason: FinishReason,
): FinalBoardSnapshot {
  return {
    grid: grid.map((row) => row.slice()),
    next: next.map((g) => ({ ...g })),
    movesCount,
    finishReason,
  };
}

/**
 * Снять ВИЗУАЛЬНЫЙ снимок in-progress доски из текущего состояния движка (вызывается после
 * каждого хода доски дня, пока стор держит живое состояние). Глубоко копирует grid + next,
 * чтобы снимок не мутировал вслед за стором. Кладётся в синхронный in-progress-маркер
 * (`saveInProgressMarker`) → при возврате к незавершённой партии доска дня рисуется
 * МГНОВЕННО из снимка, без кадра «загрузка» и переигровки (owner-bug A 2026-06-28). В отличие
 * от `snapshotFinalBoard` несёт ТЕКУЩИЙ `score` (вместо `finishReason` — партия не финальна).
 */
export function snapshotInProgressBoard(
  grid: readonly CellValue[][],
  next: readonly NextBall[],
  score: number,
  movesCount: number,
): InProgressBoardView {
  return {
    grid: grid.map((row) => row.slice()),
    next: next.map((g) => ({ ...g })),
    score,
    movesCount,
  };
}

/**
 * Пересимулировать партию из `seed + moves[]` каноном-движком и собрать финальный
 * снимок + итоговые числа. Тот же прогон, что `replayScore` на сервере, поэтому
 * счёт совпадает с `score_verified` (детерминизм). Если ход не применился
 * (повреждение / дрейф движка) — останавливаемся на достигнутом состоянии (done-
 * state деградирует мягко, не падает).
 */
export function resimulateFinalBoard(seed: number, moves: readonly Move[]): ResimulatedFinalBoard {
  const st = startState(seed);
  let applied = 0;
  for (const m of moves) {
    const res = applyMove(st, m.sr, m.sc, m.r, m.c);
    if (!res.ok) break;
    applied++;
  }
  return {
    board: snapshotFinalBoard(st.grid, st.next, applied, 'player_finish'),
    score: st.score,
    lines: st.lines,
  };
}

/**
 * Промах кэша (чужое устройство): тянем СВОЙ реплей по `short_id` и
 * пересимулируем в финальную доску. `null`, если реплей недоступен (тизер
 * запрещён для своей партии carve-out'ом, но сетевой сбой / 404 возможны).
 */
export async function fetchFinalBoardByShortId(
  shortId: string,
): Promise<ResimulatedFinalBoard | null> {
  const result = await getReplay(shortId);
  if (!result.ok || result.kind !== 'playable') return null;
  return resimulateFinalBoard(result.replay.seed, result.replay.moves);
}

/**
 * Показатели done-state из дневного лидерборда: Ранг (своя строка `me`), Лучший
 * счёт (верхняя строка), свой `short_id`. `null`, если игрок не в лидерборде /
 * сетевой сбой / гость. Ранг и лучший счёт «живые» (меняются за день), поэтому
 * тянутся свежими при каждом заходе, а не кэшируются.
 *
 * @param dateOfDay — день доски (по умолчанию серверный «сегодня»).
 */
export async function fetchOwnDailyStats(dateOfDay?: string): Promise<OwnDailyStats | null> {
  const res = await getDailyLeaderboard(dateOfDay !== undefined ? { date: dateOfDay } : {});
  if (!res.ok || !res.me) return null;
  const bestScore = res.rows.length > 0 ? res.rows[0]!.scoreVerified : res.me.scoreVerified;
  return {
    rank: res.me.rank,
    bestScore,
    scoreVerified: res.me.scoreVerified,
    linesVerified: res.me.linesVerified,
    shortId: res.me.shortId,
  };
}
